import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import posthog from 'posthog-js';
import API_URL from '../../config';
import {
  Eye,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Info,
  Lightbulb,
  ChevronRight,
  FileText,
  ShieldCheck,
} from 'lucide-react';
import ViolationsTranslatorContent from '../Violations/ViolationsTranslatorContent';
import '../../App.css';

const OVERALL_CONFIG = {
  LOW: {
    label: 'Strong profile',
    color: '#10B981',
    bg: 'var(--success-bg)',
    message: 'Your profile looks solid. Keep your documents and authority current to stay in great shape.',
  },
  MED: {
    label: 'Good—room to improve',
    color: '#F59E0B',
    bg: 'var(--warning-bg)',
    message: 'You’re in good shape. The steps below will help you present an even stronger profile.',
  },
  HIGH: {
    label: 'Needs attention',
    color: '#EF4444',
    bg: 'var(--danger-bg)',
    message: 'A few focused actions will improve how others see you. You’ve got this—tackle one item at a time.',
  },
};

const STATUS_ICONS = {
  good: CheckCircle,
  warning: AlertTriangle,
  attention: Info,
};

const ROUTES = {
  authority: '/authority',
  documents: '/documents',
  vehicles: '/vehicles',
  audit: '/audit',
  violations: '/counterparty',
};

export default function CounterpartyViewPage() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [defaultProfile, setDefaultProfile] = useState('BROKER');
  const [selectedProfile, setSelectedProfile] = useState('BROKER');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const auth = useCallback(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
    [],
  );

  const fetchProfilesAndDefault = useCallback(async () => {
    try {
      const [profRes, defaultRes] = await Promise.all([
        axios.get(`${API_URL}/api/counterparty/profiles`, auth()),
        axios.get(`${API_URL}/api/counterparty/default`, auth()),
      ]);
      setProfiles(Array.isArray(profRes.data) ? profRes.data : []);
      const def = defaultRes.data?.profile || 'BROKER';
      setDefaultProfile(def);
      setSelectedProfile(def);
    } catch {
      setProfiles([]);
      setDefaultProfile('BROKER');
      setSelectedProfile('BROKER');
    }
  }, [auth]);

  const fetchView = useCallback(
    async (profile) => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`${API_URL}/api/counterparty/view`, {
          params: { profile: profile || selectedProfile },
          ...auth(),
        });
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to load your view. Try again.');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [selectedProfile, auth],
  );

  useEffect(() => {
    fetchProfilesAndDefault();
  }, [fetchProfilesAndDefault]);

  useEffect(() => {
    posthog.capture('counterparty_view_opened');
  }, []);

  useEffect(() => {
    if (!selectedProfile) return;
    fetchView(selectedProfile);
  }, [selectedProfile, fetchView]);

  const handleProfileChange = (e) => {
    const value = e.target.value;
    setSelectedProfile(value);
  };

  const handleFixClick = () => {
    if (!data?.factors?.length) return;
    const firstNeedingWork = data.factors.find((f) => f.status !== 'good');
    if (!firstNeedingWork) return;
    const path =
      firstNeedingWork.label.includes('Authority') || firstNeedingWork.key === 'AUTHORITY_STATUS' ? ROUTES.authority
      : firstNeedingWork.label.includes('Insurance') || firstNeedingWork.key === 'INSURANCE_VALID' ? ROUTES.documents
      : firstNeedingWork.label.includes('Inspection') || firstNeedingWork.key === 'INSPECTION_RECENCY' ? ROUTES.vehicles
      : firstNeedingWork.label.includes('Violation') || firstNeedingWork.key === 'VIOLATION_TREND' ? ROUTES.violations
      : firstNeedingWork.label.includes('Audit') || firstNeedingWork.label.includes('Vehicle') ? ROUTES.vehicles
      : ROUTES.documents;
    navigate(path);
  };

  const recommendedSteps = (data?.factors || [])
    .filter((f) => f.status !== 'good' && f.howToImprove)
    .slice(0, 5)
    .map((f) => f.howToImprove);

  if (loading && !data && !profiles.length) {
    return (
      <div className="counterparty-view-page">
        <div className="counterparty-view-loading">
          <RefreshCw size={28} className="spin" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="counterparty-view-page">
        <div className="counterparty-view-error">
          <AlertTriangle size={24} />
          <p>{error}</p>
          <button type="button" className="button primary" onClick={() => fetchView(selectedProfile)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const config = data ? OVERALL_CONFIG[data.overallStatus] || OVERALL_CONFIG.MED : null;

  return (
    <div className="counterparty-view-page">
      <header className="counterparty-view-header">
        <div className="counterparty-view-header-top">
          <h1>
            <Eye size={28} />
            How Others See You
          </h1>
          <button
            type="button"
            className="button secondary"
            onClick={() => fetchView(selectedProfile)}
            disabled={loading}
          >
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
        <p className="counterparty-view-tagline">
          See what brokers, insurers, shippers, and contractors see when they evaluate your operation — with your full FMCSA violation history translated into plain English.
        </p>
        {profiles.length > 0 && (
          <div className="counterparty-view-profile-select">
            <label htmlFor="counterparty-profile">View as:</label>
            <select
              id="counterparty-profile"
              value={selectedProfile}
              onChange={handleProfileChange}
              className="counterparty-profile-select"
            >
              {profiles.map((p) => (
                <option key={p.id} value={p.name}>
                  {p.name === defaultProfile ? `${p.name} (default)` : p.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </header>

      {data && (
        <>
          <section className="counterparty-view-overall" aria-label="Overall status">
            <div
              className="counterparty-view-overall-badge"
              style={{ backgroundColor: config.bg, color: config.color }}
            >
              {config.label}
            </div>
            <p className="counterparty-view-overall-message">{config.message}</p>
          </section>

          <section className="counterparty-view-factors" aria-label="Factors">
            <h2>What they look at</h2>
            <p className="counterparty-view-factors-intro">
              Each factor affects how this type of counterparty assesses you. Improving these strengthens your profile.
            </p>
            <ul className="counterparty-view-factor-list">
              {(data.factors || []).map((factor, idx) => {
                const Icon = STATUS_ICONS[factor.status] || Info;
                return (
                  <li key={factor.key || idx} className={`counterparty-view-factor-item status-${factor.status}`}>
                    <div className="counterparty-view-factor-header">
                      <Icon size={20} className="counterparty-view-factor-icon" />
                      <span className="counterparty-view-factor-label">{factor.label}</span>
                      <span className="counterparty-view-factor-value">{factor.value}</span>
                    </div>
                    <div className="counterparty-view-factor-details">
                      <div className="counterparty-view-factor-why">
                        <strong>Why it matters</strong>
                        <p>{factor.whyItMatters}</p>
                      </div>
                      <div className="counterparty-view-factor-how">
                        <Lightbulb size={16} />
                        <strong>How to improve</strong>
                        <p>{factor.howToImprove}</p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          {recommendedSteps.length > 0 && (
            <section className="counterparty-view-next-steps">
              <h2>
                <FileText size={22} />
                Recommended next steps
              </h2>
              <p className="counterparty-view-next-steps-intro">Small actions add up. Pick one to start with.</p>
              <ul className="counterparty-view-next-steps-list">
                {recommendedSteps.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ul>
              <button type="button" className="button primary counterparty-view-cta" onClick={handleFixClick}>
                <ShieldCheck size={18} />
                Take me to fix missing items
                <ChevronRight size={18} />
              </button>
            </section>
          )}
        </>
      )}

      <hr className="counterparty-view-violation-divider" aria-hidden="true" />

      <section
        className="counterparty-violation-history"
        aria-labelledby="counterparty-violation-history-heading"
      >
        <h2 id="counterparty-violation-history-heading">Violation History</h2>
        <p className="counterparty-view-factors-intro">
          Your FMCSA inspection violations explained in plain English — with next steps to resolve each one.
        </p>
        <ViolationsTranslatorContent embedded />
      </section>
    </div>
  );
}
